import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ServiceTemplate } from '../types/entities';

type SortField = 'name' | 'estimated_duration' | 'price';
type SortDirection = 'asc' | 'desc' | 'default';

interface ServiceTemplatesTableProps {
  serviceTemplates: ServiceTemplate[];
  onEdit: (serviceTemplate: ServiceTemplate) => void;
  onDelete: (serviceTemplate: ServiceTemplate) => void;
}

const ServiceTemplatesTable: React.FC<ServiceTemplatesTableProps> = ({
  serviceTemplates,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('default');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: default -> asc -> desc -> default
      if (sortDirection === 'default') {
        setSortDirection('asc');
      } else if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection('default');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedServiceTemplates = [...serviceTemplates].sort((a, b) => {
    if (sortDirection === 'default' || !sortField) {
      return 0; // No sorting, maintain original order
    }

    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'estimated_duration':
        aValue = a.estimated_duration;
        bValue = b.estimated_duration;
        break;
      case 'price':
        aValue = parseFloat(a.price);
        bValue = parseFloat(b.price);
        break;
      default:
        return 0;
    }

    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });
  const formatPrice = (price: string): string => {
    const numPrice = Math.round(parseFloat(price));
    return `${numPrice} zł`;
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins}m`;
    } else if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${mins}m`;
    }
  };

  // Mobile card view
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sortedServiceTemplates.map((serviceTemplate) => (
          <Card key={serviceTemplate.id} sx={{ width: '100%' }}>
            <CardContent sx={{ pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                  {serviceTemplate.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    color="primary"
                    onClick={() => onEdit(serviceTemplate)}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => onDelete(serviceTemplate)}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {serviceTemplate.description && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      fontSize: '0.9rem',
                      lineHeight: 1.4,
                      maxHeight: '3.6rem', // ~3 lines
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {serviceTemplate.description}
                  </Typography>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label={formatDuration(serviceTemplate.estimated_duration)}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                    <Chip
                      label={formatPrice(serviceTemplate.price)}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  // Desktop table view
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortField === 'name'}
                direction={sortField === 'name' && sortDirection !== 'default' ? sortDirection : undefined}
                onClick={() => handleSort('name')}
                sx={{ fontWeight: 'bold' }}
              >
                {t('common.name')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ width: '40%', fontWeight: 'bold' }}>{t('common.description')}</TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'estimated_duration'}
                direction={sortField === 'estimated_duration' && sortDirection !== 'default' ? sortDirection : undefined}
                onClick={() => handleSort('estimated_duration')}
                sx={{ fontWeight: 'bold' }}
              >
                {t('common.duration')}
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'price'}
                direction={sortField === 'price' && sortDirection !== 'default' ? sortDirection : undefined}
                onClick={() => handleSort('price')}
                sx={{ fontWeight: 'bold' }}
              >
                {t('common.price')}
              </TableSortLabel>
            </TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedServiceTemplates.map((serviceTemplate) => (
            <TableRow key={serviceTemplate.id} hover>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="medium">
                  {serviceTemplate.name}
                </Typography>
              </TableCell>
              <TableCell sx={{ width: '40%' }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {serviceTemplate.description || '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDuration(serviceTemplate.estimated_duration)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="medium">
                  {formatPrice(serviceTemplate.price)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <IconButton
                    size="small"
                    onClick={() => onEdit(serviceTemplate)}
                    color="primary"
                    title={t('common.edit')}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onDelete(serviceTemplate)}
                    color="error"
                    title="Delete service template"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ServiceTemplatesTable;
